import React from "react";

const Disclaimer = () => {
  return (
    <main className="disclaimer-page">
        <h1>AI Structured Thinking &amp; Insight Disclaimer</h1>
        <p className="disclaimer-date">Last updated: January 25, 2026</p>
        <p>
          Ahavibes provides AI-generated reflections and insights designed to
          support mental performance, decision clarity, and structured thinking.
          Please read this disclaimer carefully before using our services.
        </p>

        <h2>1. Nature of the Service</h2>
        <p>
          Ahavibes offers AI-generated reflections, journaling prompts, and
          structured-thinking insights intended to encourage thoughtful
          self-reflection, decision clarity, and cognitive reset.
        </p>
        <p>The platform is designed to help users:</p>
        <ul>
          <li>Pause and observe their inner state</li>
          <li>Explore thoughts and emotions with clarity</li>
          <li>Practice structured reflection and deliberate response</li>
        </ul>
        <p>
          Ahavibes does not provide human coaching, therapy, psychological
          counseling, medical advice, diagnosis, or crisis support.
        </p>

        <h2>2. Age Requirement</h2>
        <p>
          Ahavibes is intended only for adults. You must be at least 18 years old to use the
          platform.
        </p>
        <p>
          We do not knowingly collect or process reflections from anyone under 18. If you are under
          18, do not use Ahavibes. If we learn that a user is under 18, we will take steps to
          delete their account and associated data.
        </p>

        <h2>3. Not Medical or Mental Health Advice</h2>
        <p>
          Ahavibes is not a healthcare provider and does not offer medical,
          psychiatric, or psychological services.
        </p>
        <p>AI-generated responses:</p>
        <ul>
          <li>Are for informational and reflective purposes only</li>
          <li>Do not constitute medical, psychological, or therapeutic advice</li>
          <li>Should not be used as a substitute for professional mental health care</li>
        </ul>
        <p>
          If you are experiencing emotional distress, mental health challenges,
          or a medical condition, please seek support from a licensed therapist,
          psychologist, psychiatrist, or medical professional.
        </p>
        <p>
          Using Ahavibes does not establish a therapist-patient, doctor-patient,
          or any professional healthcare relationship.
        </p>
        <p>
          Pattern signals such as the GAD-7 index on Behavioral Patterns are derived from your reflection history
          (including confirmed Quick Clarity emotions and thinking patterns) and are not administered clinical screenings
          or diagnoses.
        </p>

        <h2>4. No Crisis or Emergency Support</h2>
        <p>Ahavibes is not designed for crisis or emergency situations.</p>
        <p>
          If you are in immediate distress or experiencing thoughts of self-harm,
          suicide, or harm to others, please contact:
        </p>
        <ul>
          <li>Emergency services (911 in the U.S. or your local emergency number)</li>
          <li>A suicide prevention or mental health crisis hotline in your country</li>
        </ul>
        <p>Do not rely on Ahavibes for urgent or emergency mental health needs.</p>

        <h2>5. AI Design, Data Privacy, and Training</h2>
        <p>Your privacy is fundamental to Ahavibes.</p>
        <ul>
          <li>User reflections and entries are NOT used to train AI models</li>
          <li>Your data is not shared, sold, or reused for machine learning purposes</li>
          <li>Reflections are processed only to generate your private, in-session insight</li>
          <li>
            Optional Dictate on Quick Clarity uses your browser’s speech recognition; audio may be processed by the
            browser vendor. Ahavibes receives the resulting text in the entry box the same way as typed text and does
            not store audio
          </li>
        </ul>
        <p>
          The AI system behind Ahavibes is informed by general, non-personalized
          cognitive and structured-thinking frameworks, evidence-based reflective practices, and
          evidence-based thinking models. It does not learn from or retain
          individual user data.
        </p>

        <h2>6. How Ahavibes Is Different from General AI Tools</h2>
        <p>Ahavibes is not a general-purpose chatbot or open-ended AI prompt tool.</p>
        <p>
          While tools like ChatGPT can generate content on demand, Ahavibes is
          intentionally designed as:
        </p>
        <ul>
          <li>A guided reflective space, not a conversational assistant</li>
          <li>A paced experience that encourages slowing down</li>
          <li>A structured journey focused on how to think, not what to fix</li>
        </ul>
        <p>The reflections are curated to support:</p>
        <ul>
          <li>Self-inquiry over instant answers</li>
          <li>Awareness over quick solutions</li>
          <li>Personal insight over external validation</li>
        </ul>

        <h2>7. Philosophy Behind Ahavibes</h2>
        <p>
          Ahavibes was created with a focus on mental performance, decision clarity,
          structured thinking, and cognitive reset—not as a lifestyle brand, but as a
          practical way to steady your thinking when life gets loud.
        </p>
        <p>
          Many people look for quick reassurance from apps or trends. Ahavibes is built on a different belief:
        </p>
        <p><strong>What most people need is not fixing — but pausing.</strong></p>
        <p>The platform combines:</p>
        <ul>
          <li>Structured reflection (Event → Interpretation → Evidence → Response)</li>
          <li>Evidence-based, structured reflection to support grounded thinking</li>
        </ul>
        <p>
          Ahavibes encourages the art of thinking — observing one’s own thoughts
          with honesty, patience, and responsibility. The insights are meant to
          guide self-awareness, not replace inner work or professional support.
        </p>

        <h2>8. Encouragement to Seek Professional Support</h2>
        <p>Ahavibes is intended to complement, not replace, human support.</p>
        <p>
          We strongly encourage users to seek help from qualified professionals
          when needed, including:
        </p>
        <ul>
          <li>Therapists</li>
          <li>Counselors</li>
          <li>Medical providers</li>
          <li>Licensed mental health practitioners</li>
        </ul>
        <p>
          Use of Ahavibes should never delay, replace, or discourage professional
          care.
        </p>

        <h2>9. No Guarantees</h2>
        <p>
          Ahavibes does not guarantee specific emotional outcomes, life changes,
          or personal transformation.
        </p>
        <p>
          This is because meaningful change depends on your willingness to engage
          with the insights, reflect honestly, and apply them in your own life.
          The reflections may invite you to look at your thinking patterns,
          question familiar habits, and gently challenge your inner boundaries—
          but how you receive and act on them is entirely your choice.
        </p>
        <p>
          Ahavibes offers heartfelt guidance and reflective support, not promises
          of results. The path of growth, clarity, and self-awareness must be
          walked by you, at your own pace, toward your own destiny.
        </p>

        <h2>10. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, Ahavibes and its operators are
          not liable for any direct, indirect, incidental, consequential, or
          special damages arising from:
        </p>
        <ul>
          <li>Use of AI-generated content</li>
          <li>Interpretation of reflections or insights</li>
          <li>Decisions made based on platform outputs</li>
        </ul>
        <p>
          You remain solely responsible for your actions, interpretations, and
          the decisions you make about your mental performance and care.
        </p>

        <h2>11. Acceptance of This Disclaimer</h2>
        <p>By using Ahavibes, you acknowledge that:</p>
        <ul>
          <li>You are at least 18 years old</li>
          <li>The service is AI-generated</li>
          <li>It is not therapy, medical care, or crisis support</li>
          <li>It is not a substitute for professional help</li>
          <li>You understand and accept the limitations described above</li>
        </ul>
        <p>If you do not agree with this disclaimer, please discontinue use of the platform.</p>

        <h2>12. Governing Law and Jurisdiction</h2>
        <p>
          This disclaimer and your use of Ahavibes are governed by the laws of the State of
          Delaware, United States, without regard to conflict-of-law principles.
        </p>
        <p>
          You agree that any dispute arising from or relating to Ahavibes will be brought
          exclusively in the state or federal courts located in Delaware, and you consent to
          personal jurisdiction in those courts.
        </p>
    </main>
  );
};

export default Disclaimer;
